import {
	ElasticProperty, getLevelDepth, SuperGridValidationEntity,
} from '@aleph/supergrid';
import {
	createContext, Dispatch, SetStateAction,
} from 'react';
import MoveIcon from 'wb-reddit/assets/move.svg';
import NotAllowedIcon from 'wb-reddit/assets/not-allowed.svg';
import { ICellRendererParams, GridApi } from 'ag-grid-community';
import {
	AdAccountStatus,
	AdStatus,
	CampaignObjective,
	CampaignStatus,
	GroupStatus,
} from 'wb-spotify/graphql/generated';
import {
	AwarenessIcon,
	CancelIcon,
	CursorPointerIcon,
	DATE_PARSE_FORMAT,
	DATE_RANGE_PRESETS,
	HeadphonesIcon,
	OpenInBrowserIcon,
	VideoViewsIcon,
	translate,
} from '@aleph/shared-components';
import dayjs from 'dayjs';

export const MIN_SUPERGRID_ALLOWED_DATE = DATE_RANGE_PRESETS
	.find(range => range.label === 'Last year')?.range.from ?? dayjs().subtract(18, 'month');

export const MAX_SUPERGRID_ALLOWED_DATE = dayjs();

export const DEFAULT_SUPERGRID_MIN_DATE = dayjs().subtract(6, 'days');
export const DEFAULT_SUPERGRID_MAX_DATE = dayjs();
export const DEFAULT_SUPERGRID_DATE_RANGE = `${DEFAULT_SUPERGRID_MIN_DATE.format(DATE_PARSE_FORMAT)}:${DEFAULT_SUPERGRID_MAX_DATE.format(DATE_PARSE_FORMAT)}`;

export function isSupergridDateRangeValid(fromDate: dayjs.Dayjs, toDate: dayjs.Dayjs) {
	return fromDate.isBefore(MIN_SUPERGRID_ALLOWED_DATE) || toDate.isAfter(MAX_SUPERGRID_ALLOWED_DATE);
}

/**
 * URL params object containing a date field representing a date range
 */
export interface SuperGridParams {
	date?: string;
	[key: string]: unknown;
}

/**
 * Get the date range for the supergrid from the params
 * If the date range is not valid or missing, return the default date range
 */
export function getDateRangeForSupergridFromParams(params: SuperGridParams): string {
	// check if date field exists and is a string
	if (!params?.date || typeof params.date !== 'string') {
		return DEFAULT_SUPERGRID_DATE_RANGE;
	}

	const dateRange = params.date.split(':');

	// check if date range has exactly two parts, from and to
	if (dateRange.length !== 2) {
		return DEFAULT_SUPERGRID_DATE_RANGE;
	}

	const [fromDateStr, toDateStr] = dateRange;
	const fromDate = dayjs(fromDateStr);
	const toDate = dayjs(toDateStr);

	// check if both dates are valid
	if (!fromDate.isValid() || !toDate.isValid()) {
		return DEFAULT_SUPERGRID_DATE_RANGE;
	}

	return isSupergridDateRangeValid(fromDate, toDate) ? DEFAULT_SUPERGRID_DATE_RANGE : params.date;
}

export enum SpotifyColors {
	AD_ACCOUNT = 'hsl(var(--blue-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	adAccount = 'hsl(var(--blue-500))',
	CAMPAIGN = 'hsl(var(--campaign-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	campaign = 'hsl(var(--campaign-500))',
	GROUP = 'hsl(var(--group-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	group = 'hsl(var(--group-500))',
	AD = 'hsl(var(--ad-500))',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
	ad = 'hsl(var(--ad-500))',
}

export enum SpotifyLevel {
	AD_ACCOUNT = 'adAccount',
	CAMPAIGN = 'campaign',
	GROUP = 'group',
	AD = 'ad',
	BREAKDOWN = 'breakdown',
}

export const SpotifyLevelId = {
	[SpotifyLevel.AD_ACCOUNT]: 'clientId',
	[SpotifyLevel.CAMPAIGN]:   'campaignId',
	[SpotifyLevel.GROUP]:      'groupId',
	[SpotifyLevel.AD]:         'adId',
};

export const SPOTIFY_LEVELS_WITH_ID_FIELD = [SpotifyLevel.CAMPAIGN, SpotifyLevel.GROUP, SpotifyLevel.AD];

export enum SpotifyBreakdowns {
	ENTITIES = 'structure',
	DAILY = 'daily',
	HOURLY = 'hourly',
}

export enum SpotifyGroupBreakdowns {
	ENTITIES = 'structure',
	DAILY = 'daily',
	HOURLY = 'hourly',
	COUNTRY = 'country',
	AGE = 'age',
	GENRE = 'genre',
	PLATFORM = 'platform',
	GENDER = 'gender',
}

// Group-only breakdowns that should be reset if level is not GROUP
export const groupOnlyBreakdowns = [
	SpotifyGroupBreakdowns.COUNTRY,
	SpotifyGroupBreakdowns.AGE,
	SpotifyGroupBreakdowns.GENRE,
	SpotifyGroupBreakdowns.PLATFORM,
	SpotifyGroupBreakdowns.GENDER,
];

export enum NotificationReadStatus {
	Unread = 'Unread',
	Read = 'Read',
}

export const MAPPED_COLUMN_NAMES = {
	campaignName:                            'campaignName',
	groupName:                               'groupName',
	adName:                                  'adName',
	groupTargetingGeolocations:              'locations',
	groupTargetingCommunities:               'communities',
	groupTargetingExcludedCommunities:       'communitiesExcluded',
	groupTargetingExcludedGeolocations:      'locationsExcluded',
	groupTargetingInterests:                 'interests',
	groupTargetingKeywords:                  'keywords',
	groupTargetingExcludedKeywords:          'keywordsExcluded',
	groupTargetingCustomAudienceIds:         'customAudiences',
	groupTargetingExcludedCustomAudienceIds: 'customAudiencesExcluded',
	groupTargetingDevices:                   'devices',
	groupTargetingDevicesType:               'devices',
};

export type GraphQLErrorWithFormValidation = { debugMessage?: string, message?: string, hash?: string, form?: Record<string, { message?: string }> };

export const mapValidationErrors = (
	errors: GraphQLErrorWithFormValidation[],
	level?: SpotifyLevel,
) => errors.reduce<SuperGridValidationEntity[]>(
	(accu, error) => {
		Object.keys(error?.form ?? {}).forEach((field) => {
			const translationMessage = translate(error?.form?.[field]?.message ?? 'sc: unknown err');

			accu.push({
				id:      error.hash ?? 'hash',
				field:   MAPPED_COLUMN_NAMES[field] ?? field,
				message: translationMessage,
				level,
			});
		});

		return accu;
	},
	[],
);

export const SPOTIFY_ALWAYS_VISIBLE_COLUMNS = [
	'index',
	'breakdown',
	'entityName',
	'expand',
	'status',
	'adAccountName',
	'campaignName',
	'groupName',
	'adName',
	'breakdownName',
	'settingsElastic',
];

export enum GenericStatus {
	PendingPaused = 'PENDING_PAUSED',
	Paused = 'PAUSED',
	PendingActive = 'PENDING_ACTIVE',
	Active = 'ACTIVE',
	PendingWarning = 'PENDING_WARNING',
	Warning = 'WARNING',
	PendingError = 'PENDING_ERROR',
	Error = 'ERROR',
	Archived = 'ARCHIVED',
	PendingArchived = 'PENDING_ARCHIVED',
	Deleted = 'DELETED',
	PendingDeleted = 'PENDING_DELETED',
}

export const STATUS_CHECKED_VALUES = {
	campaign: [
		CampaignStatus.Active,
	],
	group: [
		GroupStatus.Active,
		GroupStatus.Ready,
		GroupStatus.Approved,
	],
	ad: [
		AdStatus.Active,
		AdStatus.Approved,
	],
};

export const STATUS_COLOR_PALETTES = {
	[SpotifyLevel.AD_ACCOUNT]: {
		[AdAccountStatus.Active]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdAccountStatus.GreyListed]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--yellow-500))',
		},
		[AdAccountStatus.Inactive]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdAccountStatus.SuspendListed]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--red-500))',
		},
	},
	[SpotifyLevel.CAMPAIGN]: {
		[CampaignStatus.Paused]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[CampaignStatus.Unrecognized]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--red-500))',
		},
		[CampaignStatus.Unset]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[CampaignStatus.Active]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[CampaignStatus.Archived]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--gray-0))',
		},
	},
	[SpotifyLevel.GROUP]: {
		[GroupStatus.Active]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Approved]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Archived]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Completed]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.PendingApproval]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--green-500))',
		},
		[GroupStatus.Ready]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Rejected]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--red-500))',
		},
	},
	[SpotifyLevel.AD]: {
		[AdStatus.Active]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Approved]: {
			backgroundColor: 'hsl(var(--green-500))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Archived]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Pending]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.PendingApproval]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Rejected]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--red-500))',
		},
		[AdStatus.Unrecognized]: {
			backgroundColor: 'hsl(var(--gray-300))',
			dotColor:        'hsl(var(--red-500))',
		},
	},
};

export const SYNC_PENDING_STATUS_COLOR_PALETTES = {
	[SpotifyLevel.CAMPAIGN]: {
		[CampaignStatus.Paused]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[CampaignStatus.Unrecognized]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--red-500))',
		},
		[CampaignStatus.Unset]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[CampaignStatus.Active]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[CampaignStatus.Archived]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
	},
	[SpotifyLevel.GROUP]: {
		[GroupStatus.Active]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[GroupStatus.Approved]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[GroupStatus.Archived]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Completed]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--storm-gray-400))',
		},
		[GroupStatus.PendingApproval]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[GroupStatus.Ready]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[GroupStatus.Rejected]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--red-500))',
		},
	},
	[SpotifyLevel.AD]: {
		[AdStatus.Active]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[AdStatus.Approved]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--green-500))',
		},
		[AdStatus.Archived]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Pending]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.PendingApproval]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--gray-0))',
		},
		[AdStatus.Rejected]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--red-500))',
		},
		[AdStatus.Unrecognized]: {
			backgroundColor: 'hsl(var(--storm-gray-400))',
			dotColor:        'hsl(var(--red-500))',
		},
	},
};

export const SPOTIFY_SUPERGRID_ICONS = {
	columnMovePin:   `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveMove:  `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveLeft:  `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveRight: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveGroup: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	columnMoveValue: `<img src="${MoveIcon}" alt="move" width="16"/>`,
	dropNotAllowed:  `<img src=${NotAllowedIcon} alt="not-allowed" width="16"/>`,
};

export const showXWithoutFocus = (params: ICellRendererParams, elasticProperty: ElasticProperty<SpotifyLevel>) => {
	const gridTopLevel = getLevelDepth(params.context.config, params.context?.gridTopLevel);
	const elasticPropertyLevels = elasticProperty?.levels
		.map(level => getLevelDepth(params.context.config, level) - gridTopLevel);
	const focusedRowNode = params.api?.getRenderedNodes()
		.find(node => node.rowIndex === params.api?.getFocusedCell()?.rowIndex);
	const isLevelOpenedWith = elasticProperty?.lastClicked?.[params.data?.level] === params.rowIndex;
	const ensureLowestLevel = elasticPropertyLevels && Math.max(...elasticPropertyLevels)
		=== getLevelDepth(params.context.config, params.data?.level) - gridTopLevel;
	const areEditingLevelsBeingFocused = elasticPropertyLevels?.includes(focusedRowNode?.level ?? 0);

	return isLevelOpenedWith && ensureLowestLevel && !areEditingLevelsBeingFocused;
};

export const RedditSuperGridContext = createContext<{
	gridApi?: GridApi | null,
	setGridApi: Dispatch<SetStateAction<GridApi | null | undefined>>
}>({
	gridApi:    null,
	setGridApi: () => null,
});

export const processValueToJSON = (params: { value: any }) => {
	try {
		return JSON.stringify(params.value);
	} catch (e) {
		return params.value;
	}
};

export const processValueFromJSON = (params: { value: any }) => {
	try {
		return JSON.parse(params.value);
	} catch (e) {
		return params.value;
	}
};

export const SPOTIFY_CAMPAIGN_OBJECTIVE_ICONS = {
	[CampaignObjective.Reach]: <AwarenessIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
	[CampaignObjective.EvenImpressionDelivery]: <OpenInBrowserIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
	[CampaignObjective.VideoViews]: <VideoViewsIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
	[CampaignObjective.Clicks]: <CursorPointerIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
	[CampaignObjective.PodcastStreams]: <HeadphonesIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
	[CampaignObjective.Unset]: <CancelIcon fill="hsl(var(--blue-500))"
		height={16}
		width={16}
	/>,
};

export function getAdAccountCurrency(row: any) {
	const { parent } = row;

	if (row.adAccountCurrencyCode) {
		return row.adAccountCurrencyCode;
	}

	if (parent) {
		return getAdAccountCurrency(parent);
	}

	return null;
}
